import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, RefreshCw, X } from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";
import { formatInr } from "./runPayrollUtils";

/**
 * Cross-check One Time tax Monthly Variables for the pay month.
 * Data is loaded from backend (PayrollMonthlyInput + TDS computationJson) — not UI-only.
 */
export default function OneTimeTaxModal({
  payMonth,
  darkMode = false,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!payMonth) return;
    setLoading(true);
    try {
      const res = await payrollOpsService.listOneTimeTax(payMonth);
      if (!res?.success) {
        toast.error(res?.message || "Failed to load One Time tax");
        setData(null);
        return;
      }
      setData(res.data || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load One Time tax"
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [payMonth]);

  useEffect(() => {
    load();
  }, [load]);

  if (typeof document === "undefined") return null;

  const border = darkMode
    ? "border-[hsl(var(--ds-border))]"
    : "border-[hsl(var(--ds-border))]";
  const surface = darkMode
    ? "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";
  const muted = darkMode
    ? "text-[hsl(var(--ds-muted-fg))]"
    : "text-[hsl(var(--ds-muted-fg))]";
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const summary = data?.summary || {};
  const catalog = Array.isArray(data?.catalogOneTimeComponents)
    ? data.catalogOneTimeComponents
    : [];

  return createPortal(
    <div className="fixed inset-0 z-[100080] flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16">
      <div
        className={`w-full max-w-5xl rounded-xl border shadow-xl ${border} ${surface}`}
        role="dialog"
        aria-modal="true"
        aria-label="One Time tax"
      >
        <div
          className={`flex items-start justify-between gap-3 border-b px-4 py-3 ${border}`}
        >
          <div>
            <h2 className="text-sm font-semibold">One Time tax</h2>
            <p className={`mt-0.5 text-[11px] ${muted}`}>
              Pay month {payMonth}
              {data?.financialYear ? ` · FY ${data.financialYear}` : ""}
              {" · "}
              Incremental TDS on Monthly Variables with Tax = One Time (from DB)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] ${border}`}
            >
              {loading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="space-y-3 px-4 py-3">
          {catalog.length > 0 && (
            <p className={`text-[11px] ${muted}`}>
              Structure catalog (One Time tax):{" "}
              {catalog.map((c) => c.name).join(", ")}
            </p>
          )}

          <div className="grid gap-2 sm:grid-cols-4">
            {[
              ["Employees", summary.employeeCount ?? 0],
              ["Taxable amount", formatInr(summary.totalTaxableAmount)],
              ["One Time TDS", formatInr(summary.totalOneTimeTds)],
              ["Need revalidate", summary.needsRevalidateCount ?? 0],
            ].map(([label, value]) => (
              <div
                key={label}
                className={`rounded-lg border px-3 py-2 ${border} ${
                  darkMode ? "bg-[hsl(var(--ds-muted))]/40" : "bg-[hsl(var(--ds-muted))]/30"
                }`}
              >
                <p className={`text-[10px] uppercase tracking-wide ${muted}`}>{label}</p>
                <p className="mt-0.5 text-sm font-semibold tabular-nums">{value}</p>
              </div>
            ))}
          </div>

          {loading ? (
            <div className={`flex items-center gap-2 py-8 text-xs ${muted}`}>
              <Loader2 size={14} className="animate-spin" /> Loading from payroll DB…
            </div>
          ) : rows.length === 0 ? (
            <p className={`py-6 text-center text-xs ${muted}`}>
              No One Time tax Monthly Variable amounts for {payMonth}. Add amounts under Monthly
              Variables for components configured with Tax = One Time.
            </p>
          ) : (
            <div className={`max-h-[55vh] overflow-auto rounded-lg border text-xs ${border}`}>
              <table className="w-full">
                <thead
                  className={
                    darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"
                  }
                >
                  <tr>
                    <th className="px-2 py-1.5 text-left">Code</th>
                    <th className="px-2 py-1.5 text-left">Name</th>
                    <th className="px-2 py-1.5 text-left">Components</th>
                    <th className="px-2 py-1.5 text-right">Taxable amt</th>
                    <th className="px-2 py-1.5 text-right">Regular TDS</th>
                    <th className="px-2 py-1.5 text-right">One Time TDS</th>
                    <th className="px-2 py-1.5 text-right">Computed</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.employeeId}
                      className={
                        darkMode
                          ? "border-t border-[#3C3C3C]"
                          : "border-t border-[hsl(var(--ds-border))]"
                      }
                    >
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.empCode}</td>
                      <td className="px-2 py-1.5">{r.name}</td>
                      <td className="px-2 py-1.5">
                        {(r.components || []).map((c) => (
                          <div key={`${r.employeeId}-${c.inputId || c.componentName}`}>
                            {c.componentName}: {formatInr(c.amount)}
                          </div>
                        ))}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                        {formatInr(r.oneTimeTaxableAmount)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {r.regularComputedTds != null
                          ? formatInr(r.regularComputedTds)
                          : "—"}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right tabular-nums font-semibold ${
                          Number(r.oneTimeTds) > 0 ? "text-emerald-700" : ""
                        }`}
                      >
                        {r.hasTdsRecord ? formatInr(r.oneTimeTds) : "—"}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {r.computedTds != null ? formatInr(r.computedTds) : "—"}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.needsRevalidate ? (
                          <span className="text-amber-700">Revalidate TDS</span>
                        ) : r.hasTdsRecord ? (
                          <span className="text-emerald-700">OK</span>
                        ) : (
                          <span className={muted}>No TDS row</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data?.note && (
            <p className={`text-[10px] leading-snug ${muted}`}>{data.note}</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
